import { z } from 'zod';

// src/incidents/notice.ts
function toneFor(notice) {
  if (!notice.references?.length) return "info";
  switch (notice.severity) {
    case "critical":
      return "critical";
    case "high":
    case "medium":
      return "warning";
    default:
      return "info";
  }
}
function isSubjectOfHarm(notice) {
  return notice.role === "victim" || notice.role === "recovery";
}
function chipLabelFor(notice) {
  const role = String(notice.role ?? "");
  const named = {
    exploiter: "Reported exploiter",
    intermediary: "Reported in exploit flow",
    cashout: "Reported cash-out",
    victim: "Exploit target",
    recovery: "Recovered funds"
  };
  return named[role] ?? "Named in an exploit report";
}
function bannerPropsFor(notice) {
  return {
    incidentId: notice.incident_id,
    tone: toneFor(notice),
    chipLabel: chipLabelFor(notice),
    headline: notice.headline ?? "",
    note: notice.note ?? "",
    summary: notice.summary ?? "",
    subjectOfHarm: isSubjectOfHarm(notice),
    references: notice.references ?? []
  };
}
function bannersFor(notices) {
  return (notices ?? []).filter((notice) => Boolean(notice?.headline)).map(bannerPropsFor);
}
z.object({ "label": z.string().describe("Display name of the source, e.g. 'Bubblemaps'."), "url": z.string().describe("Link to the report."), "handle": z.string().describe("Social handle including the '@', when the report was published under one. Rendered as the credit in the banner.").default("") }).describe("One public source for the incident, and who it is by.\n\nBoth halves are required to be useful and neither is sufficient: a URL with\nno attribution reads as an anonymous accusation, and a handle with no link\ncannot be checked.");
z.object({ "incident_id": z.string(), "incident_name": z.string(), "chain_id": z.string(), "address": z.string(), "role": z.any(), "severity": z.any(), "occurred_on": z.string().default(""), "summary": z.string().default(""), "note": z.string().default(""), "headline": z.string().describe("The banner's own sentence, composed server-side by :func:`commons.incidents.notice_for`. Always hedged \u2014 'reported to be involved in' \u2014 because the registry records what a source said, not a finding of fact.").default(""), "references": z.array(z.any()).optional() }).describe('The rendered banner for one address, on one incident.\n\nA flat, pre-composed shape rather than the :class:`Incident` itself, for two\nreasons. The front end must not have to re-derive wording \u2014 two clients\nphrasing the same claim differently is how a hedge ("reported to be") turns\ninto an assertion in one of them. And a viewer is entitled to the addresses\nnamed in an incident they are looking at, but not to the whole registry,\nwhich this shape simply does not carry.');
z.object({ "chain_id": z.string().describe("CAIP-2 chain id."), "address": z.string().describe("Lowercased address."), "role": z.any().default("intermediary"), "note": z.string().describe("One line on what this address specifically did, in the reporter's terms. Carried per address rather than per incident because the same incident says very different things about an exploiter and about a wallet that received one transfer from one.").default("") }).describe("One address named in an incident, and in what capacity.");
var IncidentSchema = z.object({ "id": z.string().describe("Stable slug, e.g. 'nesa_cosmos_evm_2026'."), "name": z.string().describe("Short display name, e.g. 'Nesa / Cosmos EVM'."), "summary": z.string().describe("One sentence on what happened, for the banner's tooltip.").default(""), "occurred_on": z.string().describe("ISO-8601 date (YYYY-MM-DD) the exploit happened. A date rather than a timestamp: public reporting resolves to a day, and a fabricated hour would imply a precision nobody has.").default(""), "severity": z.any().default("high"), "chains": z.array(z.string()).describe("CAIP-2 chains the incident spans.").optional(), "references": z.array(z.any()).optional(), "addresses": z.array(z.any()).optional() }).describe("A publicly reported exploit and the addresses named in it.");

// src/incidents/incident-catalog.generated.json
var incident_catalog_generated_default = {
  incidents: [
    {
      addresses: [
        {
          address: "0x9ae755d23fc948fe94c9364a2398fd508a2ab0d2",
          chain_id: "eip155:1",
          note: "Primary wallet. Bought NES on Ethereum, bridged to Nesa, inflated the balance via the Cosmos EVM vulnerability, bridged back and dispersed the proceeds through a wallet network.",
          role: "exploiter"
        },
        {
          address: "0x2816039940fd2ea2ecafce843cb2cc39e73f3fd6",
          chain_id: "eip155:1",
          note: "Received from the primary wallet and forwarded onward.",
          role: "intermediary"
        },
        {
          address: "0x83c85c3c045aedebd81a678a3ffd52794af6b414",
          chain_id: "eip155:1",
          note: "Received and forwarded large ETH amounts, including 27 ETH sent directly to the deposit feeder below.",
          role: "intermediary"
        },
        {
          address: "0xc810009a4f92ff10039a9ea9c705d2634a70446e",
          chain_id: "eip155:1",
          note: "The last non-custodial hop: gathered from several branches and sent repeated large ETH transfers into the KuCoin 17 hot wallet. Carries no exchange label of its own \u2014 its behaviour is consistent with either a per-user deposit address or an attacker-controlled feeder, and the public reporting does not settle which.",
          role: "cashout"
        }
      ],
      chains: [
        "eip155:1"
      ],
      id: "nesa_cosmos_evm_balance_inflation",
      name: "Nesa / Cosmos EVM balance inflation",
      occurred_on: "2026-09-04",
      references: [
        {
          handle: "@bubblemaps",
          label: "Bubblemaps",
          url: "https://x.com/bubblemaps/status/2092613254912852356"
        },
        {
          handle: "",
          label: "Cosmos security advisory GHSA-7g4w-cg88-2cq2",
          url: "https://github.com/cosmos/security/blob/main/communications/cosmos_evm_GHSA-7g4w-cg88-2cq2_post_mortem.md"
        }
      ],
      severity: "critical",
      summary: "A balance-inflation bug in Cosmos EVM let an attacker multiply a bridged NES position roughly 200x on Nesa Chain, bridge it back to Ethereum and sell it into the open market."
    }
  ]
};

// src/incidents/catalog.ts
var CatalogSchema = z.object({ incidents: z.array(IncidentSchema).default([]) });
var BUNDLED_INCIDENTS = Object.freeze(
  [...CatalogSchema.parse(incident_catalog_generated_default).incidents].sort((a, b) => {
    const left = a.occurred_on || "";
    const right = b.occurred_on || "";
    if (left === right) return a.id.localeCompare(b.id);
    if (!left) return 1;
    if (!right) return -1;
    return right.localeCompare(left);
  })
);
function bundledIncident(id) {
  return BUNDLED_INCIDENTS.find((incident) => incident.id === id);
}
function incidentAccounts(incident) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const entry of incident.addresses ?? []) {
    const address = String(entry.address ?? "").trim();
    const chain = String(entry.chain_id ?? "").trim();
    if (!address || !chain) continue;
    const account = `${chain}:${address}`.toLowerCase();
    if (seen.has(account)) continue;
    seen.add(account);
    out.push(account);
  }
  return out;
}
function incidentRoleCounts(incident) {
  const counts = {};
  for (const entry of incident.addresses ?? []) {
    const role = String(entry.role ?? "") || "unknown";
    counts[role] = (counts[role] ?? 0) + 1;
  }
  return counts;
}

export { BUNDLED_INCIDENTS, bannerPropsFor, bannersFor, bundledIncident, chipLabelFor, incidentAccounts, incidentRoleCounts, isSubjectOfHarm, toneFor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map